import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronDown, RefreshCw, Save, Trophy } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";
import { getMarketWindowForNow } from "../utils/marketStatus";

function AdminResults() {
  const [markets, setMarkets] = useState([]);
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [marketMenuOpen, setMarketMenuOpen] = useState(false);
  const [form, setForm] = useState({
    result_date: getBusinessResultDate(),
    market_id: "",
    session: "OPEN",
    pana: "",
    digit: "",
  });

  const marketMenuRef = useRef(null);
  const selectedMarket = markets.find((m) => String(m.id) === String(form.market_id));

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const fn = (e) => { if (marketMenuRef.current && !marketMenuRef.current.contains(e.target)) setMarketMenuOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  useEffect(() => {
    if (markets.length && !markets.some((m) => String(m.id) === String(form.market_id))) {
      setForm((c) => ({ ...c, market_id: String(markets[0].id) }));
    }
  }, [markets, form.market_id]);

  const loadAll = async () => {
    await Promise.all([loadMarkets(), loadResults()]);
  };

  const loadMarkets = async () => {
    try {
      const res = await API.get("/admin/markets");
      setMarkets(Array.isArray(res.data) ? res.data.filter((m) => (m.market_type || "normal") !== "starline" && m.is_active !== 0).sort((a, b) => String(a.open_time || "").localeCompare(String(b.open_time || ""))) : []);
    } catch { setMarkets([]); }
  };

  const loadResults = async () => {
    try {
      const res = await API.get("/admin/results");
      setResults(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch { setResults([]); }
  };

  const updateForm = (key, value) => {
    setForm((c) => {
      const next = { ...c, [key]: value };
      if (key === "pana") next.digit = calculateDigit(value);
      return next;
    });
  };

  const declareResult = async (e) => {
    e.preventDefault();
    if (!form.market_id || !form.result_date || !form.session || !form.pana || form.digit === "") {
      alert("Fill all fields");
      return;
    }
    setSaving(true);
    try {
      await API.post("/admin/results/declare", form);
      await loadResults();
      setForm((c) => ({ ...c, pana: "", digit: "" }));
      alert("Result declared successfully!");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to declare result");
    } finally {
      setSaving(false);
    }
  };

  const deleteResult = async (item, session) => {
    const s = String(session || "").toUpperCase();
    const d = String(item.result_date || "").slice(0, 10);
    if (!window.confirm(`Delete ${s} result for ${item.market_name} on ${formatDate(d)}?\n\nWinning payouts will be reversed.`)) return;
    try {
      await API.delete(`/admin/results/${item.market_id}|${d}|${s}`);
      await loadResults();
      alert(`${s} result deleted.`);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete result");
    }
  };

  const resultRowsForDate = results.filter((r) => String(r.result_date || "").slice(0, 10) === form.result_date);

  return (
    <AdminLayout>
      <div style={page}>
        {/* Header */}
        <div style={pageHeader}>
          <div>
            <h1 style={titleStyle}>Result Declaration</h1>
            <p style={subtitleStyle}>Declare & manage market results anytime</p>
          </div>
          <button type="button" style={refreshBtn} onClick={loadAll}><RefreshCw size={15} /> Refresh</button>
        </div>

        {/* Declare form */}
        <section style={formCard}>
          <div style={formCardHead}>
            <Trophy size={20} color="#f97316" />
            <h2 style={formCardTitle}>Declare Result</h2>
          </div>

          <form onSubmit={declareResult}>
            <div style={formGrid}>
              {/* Date */}
              <div style={fieldWrap}>
                <label style={fieldLabel}>Date</label>
                <div style={inputBox}>
                  <CalendarDays size={16} color="#94a3b8" />
                  <input type="date" value={form.result_date} onChange={(e) => updateForm("result_date", e.target.value)} style={inputBare} />
                </div>
              </div>

              {/* Market */}
              <div style={fieldWrap}>
                <label style={fieldLabel}>Market</label>
                <div style={{ position: "relative" }} ref={marketMenuRef}>
                  <button type="button" style={dropBtn} onClick={() => setMarketMenuOpen((v) => !v)}>
                    {selectedMarket ? (
                      <div style={mCell}>
                        <span style={{ ...mBadge, ...marketColor(selectedMarket.name) }}>{initials(selectedMarket.name)}</span>
                        <span style={mName}>{selectedMarket.name}</span>
                      </div>
                    ) : <span style={{ color: "#94a3b8" }}>Select market</span>}
                    <ChevronDown size={16} color="#64748b" />
                  </button>
                  {marketMenuOpen && (
                    <div style={dropMenu}>
                      {markets.map((m) => (
                        <button key={m.id} type="button" style={dropItem(String(m.id) === String(form.market_id))} onClick={() => { updateForm("market_id", String(m.id)); setMarketMenuOpen(false); }}>
                          <span style={{ ...mBadge, ...marketColor(m.name) }}>{initials(m.name)}</span>
                          <div>
                            <div style={mName}>{m.name}</div>
                            <div style={mTime}>{fmtTime(getMarketWindowForNow(m, new Date()).open_time || m.open_time)} – {fmtTime(getMarketWindowForNow(m, new Date()).close_time || m.close_time)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Session */}
              <div style={fieldWrap}>
                <label style={fieldLabel}>Session</label>
                <div style={sessionRow}>
                  <button type="button" style={form.session === "OPEN" ? sessionActive("open") : sessionInactive("open")} onClick={() => updateForm("session", "OPEN")}>OPEN</button>
                  <button type="button" style={form.session === "CLOSE" ? sessionActive("close") : sessionInactive("close")} onClick={() => updateForm("session", "CLOSE")}>CLOSE</button>
                </div>
              </div>

              {/* Pana */}
              <div style={fieldWrap}>
                <label style={fieldLabel}>Pana</label>
                <input style={inputBox} value={form.pana} onChange={(e) => updateForm("pana", e.target.value.replace(/\D/g, "").slice(0, 3))} placeholder="e.g. 127" inputMode="numeric" />
              </div>

              {/* Digit */}
              <div style={fieldWrap}>
                <label style={fieldLabel}>Digit (auto)</label>
                <input style={{ ...inputBox, background: "#f8fafc", color: "#ea580c", fontWeight: 800 }} value={form.digit} onChange={(e) => updateForm("digit", e.target.value.replace(/\D/g, "").slice(0, 1))} placeholder="–" />
              </div>
            </div>

            {/* Preview strip */}
            {(form.pana || form.digit) && (
              <div style={previewStrip}>
                <div style={previewBox}>
                  <span style={previewLbl}>PANA</span>
                  <strong style={previewPana}>{form.pana || "---"}</strong>
                </div>
                <div style={previewDivider} />
                <div style={previewBox}>
                  <span style={previewLbl}>DIGIT</span>
                  <strong style={previewDigit}>{form.digit || "-"}</strong>
                </div>
                <div style={previewDivider} />
                <div style={previewBox}>
                  <span style={previewLbl}>SESSION</span>
                  <strong style={{ ...previewSession, color: form.session === "OPEN" ? "#16a34a" : "#dc2626" }}>{form.session}</strong>
                </div>
              </div>
            )}

            <button type="submit" style={form.pana && form.digit ? declareBtn : { ...declareBtn, opacity: 0.5 }} disabled={saving || !form.pana || !form.digit}>
              <Save size={18} /> {saving ? "Saving..." : "Declare Result"}
            </button>
          </form>
        </section>

        {/* Results history */}
        <section style={historyCard}>
          <div style={historyHead}>
            <h2 style={formCardTitle}>Declared Results ({resultRowsForDate.length} for {form.result_date})</h2>
          </div>
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  {["#", "Market", "Date", "Open Result", "Close Result", "By", "Action"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resultRowsForDate.map((item, i) => (
                  <tr key={item.id} style={tr}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>
                      <div style={mCell}>
                        <span style={{ ...mBadge, ...marketColor(item.market_name) }}>{initials(item.market_name)}</span>
                        <strong style={mName}>{item.market_name}</strong>
                      </div>
                    </td>
                    <td style={td}>{formatDate(item.result_date)}</td>
                    <td style={td}>
                      {item.open_pana ? (
                        <span style={resultOpen}>{item.open_pana} – {item.open_digit}</span>
                      ) : <span style={naDash}>–</span>}
                    </td>
                    <td style={td}>
                      {item.close_pana ? (
                        <span style={resultClose}>{item.close_pana} – {item.close_digit}</span>
                      ) : <span style={naDash}>–</span>}
                    </td>
                    <td style={td}>
                      <span style={byText}>{item.close_declared_by_name || item.open_declared_by_name || "Admin"}</span>
                    </td>
                    <td style={td}>
                      <div style={actRow}>
                        {item.open_pana && <button type="button" style={delOpenBtn} onClick={() => deleteResult(item, "OPEN")}>Del Open</button>}
                        {item.close_pana && <button type="button" style={delCloseBtn} onClick={() => deleteResult(item, "CLOSE")}>Del Close</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!resultRowsForDate.length && (
                  <tr><td colSpan={7} style={emptyCell}>No results declared yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}

const calculateDigit = (pana) => {
  const d = String(pana || "").replace(/\D/g, "").slice(0, 3).split("").map(Number);
  return d.length ? String(d.reduce((s, n) => s + n, 0) % 10) : "";
};

const getBusinessResultDate = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hour12: false }).formatToParts(now);
  const map = Object.fromEntries(parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]));
  const base = new Date(Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day)));
  if (Number(map.hour) < 2) base.setUTCDate(base.getUTCDate() - 1);
  return base.toISOString().slice(0, 10);
};

const formatDate = (v) => { if (!v) return "–"; const [y, m, d] = String(v).slice(0, 10).split("-"); return `${d}/${m}/${y}`; };
const fmtTime = (v) => { if (!v) return "--:--"; const [h, m = "00"] = String(v).split(":"); const n = Number(h); return `${String(((n + 11) % 12) + 1).padStart(2, "0")}:${m} ${n >= 12 ? "pm" : "am"}`; };
const initials = (name = "") => String(name || "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "MK";
const marketColor = (name = "") => {
  const k = String(name || "").toUpperCase();
  if (k.includes("SRIDEVI")) return { background: "linear-gradient(135deg,#22c55e,#16a34a)" };
  if (k.includes("MILAN")) return { background: "linear-gradient(135deg,#ea580c,#a855f7)" };
  if (k.includes("KALYAN")) return { background: "linear-gradient(135deg,#ef4444,#f97316)" };
  if (k.includes("RAJDHANI")) return { background: "linear-gradient(135deg,#0ea5e9,#16a34a)" };
  return { background: "linear-gradient(135deg,#ea580c,#ea580c)" };
};

const sessionActive = (t) => ({ flex: 1, height: 46, border: 0, borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: "pointer", background: t === "open" ? "#16a34a" : "#dc2626", color: "#fff", boxShadow: t === "open" ? "0 4px 14px rgba(22,163,74,0.3)" : "0 4px 14px rgba(220,38,38,0.3)" });
const sessionInactive = (t) => ({ flex: 1, height: 46, border: t === "open" ? "1.5px solid #bbf7d0" : "1.5px solid #fecaca", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", background: t === "open" ? "#f0fdf4" : "#fff5f5", color: t === "open" ? "#16a34a" : "#dc2626" });

const page = { display: "grid", gap: 20 };
const pageHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const titleStyle = { margin: 0, fontSize: 22, fontWeight: 800, color: "#431407" };
const subtitleStyle = { margin: "4px 0 0", color: "#64748b", fontSize: 13 };
const refreshBtn = { height: 36, padding: "0 16px", border: "1px solid #e5e7eb", background: "#fff", borderRadius: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" };

const formCard = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 20, padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" };
const formCardHead = { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 };
const formCardTitle = { margin: 0, fontSize: 17, fontWeight: 800, color: "#431407" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 14, marginBottom: 16 };
const fieldWrap = { display: "grid", gap: 6 };
const fieldLabel = { fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" };
const inputBox = { height: 46, border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "0 14px", display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", fontSize: 15, fontWeight: 600, color: "#0f172a", boxSizing: "border-box", width: "100%", outline: "none" };
const inputBare = { border: 0, outline: "none", width: "100%", background: "transparent", fontSize: 14, color: "#0f172a" };
const sessionRow = { display: "flex", gap: 8, height: 46 };
const dropBtn = { width: "100%", height: 46, border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "0 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#f8fafc", cursor: "pointer", fontSize: 13 };
const dropMenu = { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, maxHeight: 260, overflowY: "auto", background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,0.12)", zIndex: 30 };
const dropItem = (active) => ({ width: "100%", border: 0, borderBottom: "1px solid #f1f5f9", background: active ? "#f0fdf4" : "#fff", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left" });
const mCell = { display: "flex", alignItems: "center", gap: 10 };
const mBadge = { width: 32, height: 32, borderRadius: 8, display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontSize: 12, flexShrink: 0 };
const mName = { fontWeight: 700, fontSize: 13, color: "#0f172a" };
const mTime = { fontSize: 11, color: "#94a3b8", marginTop: 2 };

const previewStrip = { display: "flex", borderRadius: 16, overflow: "hidden", border: "1.5px solid #e2e8f0", marginBottom: 16 };
const previewBox = { flex: 1, padding: "14px 16px", display: "grid", gap: 4, alignContent: "center", background: "#f8fafc" };
const previewLbl = { fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" };
const previewPana = { fontSize: 34, fontWeight: 900, color: "#ea580c", lineHeight: 1 };
const previewDigit = { fontSize: 34, fontWeight: 900, color: "#16a34a", lineHeight: 1 };
const previewSession = { fontSize: 22, fontWeight: 900, lineHeight: 1 };
const previewDivider = { width: 1, background: "#e2e8f0", flexShrink: 0 };
const declareBtn = { width: "100%", height: 52, border: 0, borderRadius: 14, background: "linear-gradient(135deg,#431407,#ea580c)", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: "0 6px 20px rgba(79,70,229,0.3)" };

const historyCard = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 20, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" };
const historyHead = { padding: "18px 22px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" };
const tableWrap = { overflowX: "auto" };
const table = { width: "100%", borderCollapse: "collapse", minWidth: 700 };
const th = { padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" };
const tr = { borderBottom: "1px solid #f1f5f9" };
const td = { padding: "13px 14px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const resultOpen = { fontWeight: 800, color: "#16a34a", background: "#dcfce7", padding: "4px 10px", borderRadius: 8 };
const resultClose = { fontWeight: 800, color: "#dc2626", background: "#fee2e2", padding: "4px 10px", borderRadius: 8 };
const naDash = { color: "#d1d5db" };
const byText = { fontSize: 12, color: "#94a3b8" };
const actRow = { display: "flex", gap: 6, flexWrap: "wrap" };
const delOpenBtn = { height: 30, padding: "0 10px", border: 0, borderRadius: 7, background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 11, cursor: "pointer" };
const delCloseBtn = { ...delOpenBtn, background: "#ef4444" };
const emptyCell = { padding: "36px", textAlign: "center", color: "#94a3b8", fontSize: 13 };

export default AdminResults;
